import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import InlineStatusSelect from './InlineStatusSelect';
import { PlanificationCell } from './shared/DossierPlanification';
import api, { demandes as demandesApi } from '../services/api';

/* ── Logique de groupement ── */

function daysSince(dateStr) {
  if (!dateStr) return Infinity;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function groupEntries(entries) {
  const sections = [
    { id: 'a_replanifier', title: 'À replanifier — aucune intervention à venir', icon: '📆', entries: [] },
    { id: 'vo_or',        title: 'VO avec OR en cours',     icon: '🔧', entries: [] },
    { id: 'vo_sans_or',   title: 'VO sans OR en cours',     icon: '🅿️', entries: [] },
    { id: 'facture_30',   title: 'Facturés < 30 jours',     icon: '✅', entries: [] },
    { id: 'vigilance_60', title: 'Facturé entre 30 et 60 jours',  icon: '⚠️', entries: [] },
    { id: 'demandes',     title: 'Demandes clients en cours', icon: '📩', entries: [] },
    { id: 'litiges',      title: 'Litiges',                 icon: '⚖️', entries: [] },
    { id: 'takata',       title: 'Takata',                  icon: '🚨', entries: [] },
    { id: 'epaves',       title: 'Épaves',                  icon: '🚗', entries: [] },
    { id: 'sans_info',    title: 'Sans aucune information',  icon: '❓', entries: [] },
  ];

  // Aujourd'hui en AAAA-MM-JJ, heure LOCALE : `toISOString()` donnerait la date
  // UTC et basculerait d'un jour en soiree aux Antilles.
  const auj = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  })();

  for (const e of entries) {
    let matched = false;

    // Section 1 — Vehicules a replanifier.
    // `date_planification` est la derniere intervention du vehicule (la plus
    // recente) : si elle est ANTERIEURE a aujourd'hui, il n'en existe aucune
    // aujourd'hui ou apres. Un vehicule sans aucun dossier est evidemment
    // candidat lui aussi. Les dossiers RESTITUES sont ecartes : le vehicule est
    // rendu au client, il n'y a plus rien a replanifier.
    // Section transverse : elle ne pose pas `matched`, pour ne pas modifier le
    // contenu de « Sans aucune information ».
    const dejaRestitue = (e.planning_statut_nom || '').toLowerCase().startsWith('restitu');
    const planifieAVenir = !!e.date_planification && e.date_planification >= auj;
    if (!planifieAVenir && !dejaRestitue) {
      sections[0].entries.push(e);
    }

    // Section 1 & 2 — VO
    if (e.has_vo) {
      matched = true;
      if (e.or_en_cours_id) {
        sections[1].entries.push(e);
      } else {
        sections[2].entries.push(e);
      }
    }

    // Section 3 & 4 — Facturés récemment sans retour atelier
    const factureDays = daysSince(e.derniere_facture_date);
    if (factureDays < 60 && e.derniere_facture_date) {
      const hasRetourAtelier = e.or_en_cours_id && e.or_date_creation &&
        new Date(e.or_date_creation) > new Date(e.derniere_facture_date);

      if (!hasRetourAtelier) {
        matched = true;
        if (factureDays < 30) {
          sections[3].entries.push(e);
        } else {
          sections[4].entries.push(e);
        }
      }
    }

    // Section 5 — Demandes clients en cours
    if (e.demande_en_cours_id) {
      matched = true;
      sections[5].entries.push(e);
    }

    // Section 6 — Litiges
    if (e.litige_id) {
      matched = true;
      sections[6].entries.push(e);
    }

    // Section 7 — Takata
    if (e.has_takata) {
      matched = true;
      sections[7].entries.push(e);
    }

    // Section 8 — Épaves
    if (e.statut === 'epave') {
      matched = true;
      sections[8].entries.push(e);
    }

    // OR en cours (sans être VO) = aussi une info
    if (e.or_en_cours_id && !e.has_vo) matched = true;
    // Facture ancienne (>60j) = a quand même une trace
    if (e.derniere_facture_date) matched = true;
    // Statut modifié (différent de 'nouveau' ou null) = info connue
    if (e.statut && e.statut !== 'nouveau') matched = true;

    // Section 9 — Sans aucune information
    if (!matched) {
      sections[9].entries.push(e);
    }
  }

  // Tri par date facture DESC pour toutes les sections
  const sortByFacture = (a, b) => (b.derniere_facture_date || '').localeCompare(a.derniere_facture_date || '');
  sections.forEach(s => s.entries.sort(sortByFacture));
  // « À replanifier » se lit autrement : le plus ancien au parking d'abord,
  // c'est lui qui attend depuis le plus longtemps.
  const aReplanifier = sections.find(s => s.id === 'a_replanifier');
  if (aReplanifier) {
    aReplanifier.entries.sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
  }

  return sections;
}

/* ── Helpers ── */

function getAnciennete(created_at) {
  if (!created_at) return { label: '-', cls: '' };
  const days = Math.floor((Date.now() - new Date(created_at).getTime()) / 86400000);
  if (days < 10)  return { label: `${days}j`, cls: 'age-lt10' };
  if (days < 30)  return { label: `${days}j`, cls: 'age-lt30' };
  if (days < 90)  return { label: `${days}j`, cls: 'age-lt90' };
                  return { label: `${days}j`, cls: 'age-gte90' };
}

function formatDate(s) {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/* ── Export PDF ── */

function exportPDF(sections, concession, facturesBadgeMap = {}) {
  const doc = new jsPDF('landscape', 'mm', 'a4');
  const pageW = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

  // En-tête première page
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text('Rapport Inventaire Parking', 14, 18);
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  doc.text(`Date : ${today}`, 14, 25);
  if (concession) {
    doc.text(`Concession : ${concession}`, 14, 31);
  }

  // Synthèse
  let startY = concession ? 38 : 32;
  doc.setFontSize(11);
  doc.setFont(undefined, 'bold');
  doc.text('Synthèse', 14, startY);
  startY += 3;

  const synthData = sections.map(s => [
    `${s.icon} ${s.title}`,
    String(s.entries.length)
  ]);
  autoTable(doc, {
    startY,
    head: [['Section', 'Nombre']],
    body: synthData,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [245, 244, 237], textColor: [30, 30, 30], fontStyle: 'bold' },
    columnStyles: { 1: { halign: 'center', cellWidth: 25 } },
    margin: { left: 14, right: 14 },
  });

  // Chaque section
  const columns = [
    { header: 'Identifiant', dataKey: 'identifiant' },
    { header: 'Marque', dataKey: 'marque' },
    { header: 'Client', dataKey: 'nom_client' },
    { header: 'Statut', dataKey: 'statut' },
    { header: 'Ancienneté', dataKey: 'anciennete' },
    { header: 'OR Date', dataKey: 'or_date' },
    { header: 'Facture', dataKey: 'facture_date' },
    { header: 'Réceptionnaire', dataKey: 'receptionnaire' },
    // Reprise de la colonne ecran : sans elle, le PDF du rapport
    // « À replanifier » ne dirait pas de quand date la derniere intervention.
    { header: 'Planification', dataKey: 'planification' },
  ];

  const pageH = doc.internal.pageSize.getHeight();

  for (const section of sections) {
    if (section.entries.length === 0) continue;

    // Position après le dernier tableau
    const cursorY = doc.lastAutoTable?.finalY || startY + 10;

    // Saut de page si pas assez de place pour titre + header tableau (~25mm)
    let sectionY;
    if (cursorY + 25 > pageH - 15) {
      doc.addPage();
      sectionY = 20;
    } else {
      sectionY = cursorY + 12;
    }

    doc.setFontSize(12);
    doc.setFont(undefined, 'bold');
    doc.text(`${section.title} (${section.entries.length})`, 14, sectionY);

    const isDemandes = section.id === 'demandes';
    const sectionColumns = isDemandes
      ? [...columns, { header: 'Demande', dataKey: 'demande_info' }]
      : columns;

    const rows = section.entries.map(e => {
      const row = {
        identifiant: e.identifiant || '—',
        marque: (e.marque || '—').toUpperCase(),
        nom_client: e.nom_client || '—',
        statut: e.statut || '—',
        anciennete: getAnciennete(e.created_at).label,
        concession: e.concession || '—',
        or_date: formatDate(e.or_date_creation),
        facture_date: formatDate(e.derniere_facture_date),
        receptionnaire: facturesBadgeMap[(e.identifiant || '').toUpperCase()]?.receptionnaire || '—',
        planification: e.date_planification ? formatDate(e.date_planification) : '—',
      };
      if (isDemandes) {
        const parts = [];
        if (e.demande_en_cours_sujet) parts.push(`[${e.demande_en_cours_sujet}]`);
        if (e.demande_en_cours_commentaire) parts.push(e.demande_en_cours_commentaire);
        row.demande_info = parts.join(' ') || '—';
      }
      return row;
    });

    autoTable(doc, {
      startY: sectionY + 4,
      columns: sectionColumns,
      body: rows,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [245, 244, 237], textColor: [30, 30, 30], fontStyle: 'bold' },
      margin: { left: 14, right: 14 },
    });
  }

  // Pieds de page (numéros)
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont(undefined, 'normal');
    doc.text(`Page ${i} / ${totalPages}`, pageW - 30, doc.internal.pageSize.getHeight() - 8);
  }

  doc.save(`inventaire_parking_${today.replace(/\//g, '-')}.pdf`);
}

/* ── Panneau consultation demande ── */

function DemandePanel({ demandeId, user, onClose, onArchived }) {
  const [demande, setDemande] = useState(null);
  const [commentaires, setCommentaires] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [sending, setSending] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [demRes, comRes] = await Promise.all([
        demandesApi.getById(demandeId),
        demandesApi.getCommentaires(demandeId),
      ]);
      setDemande(demRes.data.demande);
      setCommentaires(comRes.data.commentaires || []);
    } catch (err) {
      console.error('Erreur chargement demande:', err);
    } finally {
      setLoading(false);
    }
  }, [demandeId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddComment = async () => {
    if (!newComment.trim() || sending) return;
    setSending(true);
    try {
      await demandesApi.addCommentaire(demandeId, newComment.trim());
      setNewComment('');
      const comRes = await demandesApi.getCommentaires(demandeId);
      setCommentaires(comRes.data.commentaires || []);
    } catch (err) {
      console.error('Erreur ajout commentaire:', err);
    } finally {
      setSending(false);
    }
  };

  const handleArchive = async () => {
    if (archiving) return;
    if (!window.confirm('Archiver cette demande ?')) return;
    setArchiving(true);
    try {
      await demandesApi.archive(demandeId);
      onArchived?.();
      onClose();
    } catch (err) {
      console.error('Erreur archivage:', err);
      setArchiving(false);
    }
  };

  const fmtDate = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const fmtDateTime = (s) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content demande-panel" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>📩 Demande client</h2>
          <button onClick={onClose} className="modal-close-btn" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Chargement...</div>
        ) : !demande ? (
          <div style={{ textAlign: 'center', padding: 24, color: '#94a3b8' }}>Demande introuvable</div>
        ) : (
          <>
            {/* Infos principales */}
            <div className="demande-panel-grid">
              <div className="demande-panel-field">
                <span className="demande-panel-label">Sujet</span>
                <span className="demande-panel-value">{demande.sujet || '—'}</span>
              </div>
              <div className="demande-panel-field">
                <span className="demande-panel-label">Date</span>
                <span className="demande-panel-value">{fmtDate(demande.date_demande)}</span>
              </div>
              <div className="demande-panel-field">
                <span className="demande-panel-label">Client</span>
                <span className="demande-panel-value">{[demande.nom, demande.prenom].filter(Boolean).join(' ') || demande.client || '—'}</span>
              </div>
              <div className="demande-panel-field">
                <span className="demande-panel-label">Téléphone</span>
                <span className="demande-panel-value">{demande.telephone || '—'}</span>
              </div>
              <div className="demande-panel-field">
                <span className="demande-panel-label">Immatriculation</span>
                <span className="demande-panel-value">{demande.immatriculation || '—'}</span>
              </div>
              <div className="demande-panel-field">
                <span className="demande-panel-label">Appels</span>
                <span className="demande-panel-value">{demande.nb_appels || 1}</span>
              </div>
            </div>

            {/* Commentaire initial */}
            {demande.commentaire_initial && (
              <div style={{ margin: '12px 0', padding: '10px 12px', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Commentaire initial</div>
                <div style={{ fontSize: 13, color: '#1e293b', whiteSpace: 'pre-wrap' }}>{demande.commentaire_initial}</div>
              </div>
            )}

            {/* Historique commentaires */}
            <div style={{ margin: '12px 0' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>
                Commentaires ({commentaires.length})
              </div>
              <div className="demande-panel-comments">
                {commentaires.length === 0 ? (
                  <div style={{ fontSize: 12, color: '#94a3b8', padding: '8px 0' }}>Aucun commentaire</div>
                ) : commentaires.map(c => (
                  <div key={c.id} className="demande-panel-comment">
                    <div className="demande-panel-comment-meta">
                      <span>{c.user_nom} {c.user_prenom || ''}</span>
                      <span>{fmtDateTime(c.created_at)}</span>
                    </div>
                    <div className="demande-panel-comment-text">{c.commentaire}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Ajout commentaire */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <input
                type="text"
                className="demande-panel-input"
                placeholder="Ajouter un commentaire..."
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddComment(); }}
              />
              <button className="btn-primary" onClick={handleAddComment} disabled={sending || !newComment.trim()} style={{ whiteSpace: 'nowrap', fontSize: 13, padding: '8px 14px' }}>
                Envoyer
              </button>
            </div>

            {/* Archiver */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn-secondary"
                onClick={handleArchive}
                disabled={archiving}
                style={{ fontSize: 13, color: '#dc2626' }}
              >
                {archiving ? 'Archivage...' : '📁 Archiver cette demande'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Composant principal ── */

export default function ParkingRapportView({
  entries,
  onRowClick,
  statuts,
  responsables,
  concessionFilter,
  user,
  onRefresh,
  facturesBadgeMap = {},
  onStatusChange,
  // Ouverture du dossier de planification, fournie par Gestion Parking : la
  // colonne Planification se comporte ici comme dans le tableau principal.
  onOuvrirDossier = () => {},
}) {
  const [collapsed, setCollapsed] = useState({
    a_replanifier: true,
    vo_or: true, vo_sans_or: true, facture_30: true, vigilance_60: true,
    demandes: true, litiges: true, takata: true, epaves: true, sans_info: true,
  });
  const [demandeModalId, setDemandeModalId] = useState(null);
  const [factureDetail, setFactureDetail] = useState(null);

  const sections = useMemo(() => groupEntries(entries), [entries]);
  const totalTagged = useMemo(() => {
    const ids = new Set();
    sections.forEach(s => s.entries.forEach(e => ids.add(e.id)));
    return ids.size;
  }, [sections]);

  const toggleSection = (id) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleExportPDF = () => {
    exportPDF(sections, concessionFilter, facturesBadgeMap);
  };

  return (
    <div className="rapport-view">

      {/* ── Barre d'actions ── */}
      <div className="rapport-toolbar">
        <div className="rapport-stats">
          <span className="rapport-stat-total">
            {totalTagged} véhicule{totalTagged !== 1 ? 's' : ''} classifié{totalTagged !== 1 ? 's' : ''}
            {' '}sur {entries.length}
          </span>
        </div>
        <button className="btn-primary rapport-export-btn" onClick={handleExportPDF}>
          📄 Exporter PDF
        </button>
      </div>

      {/* ── Synthèse compteurs ── */}
      <div className="rapport-synthese">
        {sections.map(s => (
          <div
            key={s.id}
            className={`rapport-synthese-card ${s.entries.length === 0 ? 'card-empty' : ''}`}
            onClick={() => s.entries.length > 0 && toggleSection(s.id)}
            style={{ cursor: s.entries.length > 0 ? 'pointer' : 'default' }}
          >
            <span className="synthese-icon">{s.icon}</span>
            <span className="synthese-count">{s.entries.length}</span>
            <span className="synthese-label">{s.title}</span>
          </div>
        ))}
      </div>

      {/* ── Sections accordéon ── */}
      {sections.map(section => {
        if (section.entries.length === 0) return null;
        const isCollapsed = collapsed[section.id];

        return (
          <div key={section.id} className="rapport-section">
            <div
              className="rapport-section-header"
              onClick={() => toggleSection(section.id)}
            >
              <span className="section-toggle">{isCollapsed ? '▶' : '▼'}</span>
              <span className="section-icon">{section.icon}</span>
              <span className="section-title">{section.title}</span>
              <span className="section-count">{section.entries.length}</span>
            </div>

            {!isCollapsed && (
              <div className="table-container">
                <table className="parking-table rapport-table">
                  <thead>
                    <tr>
                      <th>Identifiant</th>
                      <th>Marque</th>
                      <th>Client</th>
                      <th>Statut</th>
                      <th>Ancienneté</th>
                      <th>OR en cours</th>
                      <th>Pièces</th>
                      <th>Facture</th>
                      <th>Réceptionnaire</th>
                      {/* Colonne reprise du tableau principal : c'est elle qui
                          porte le rapport « À replanifier ». */}
                      <th>Planification</th>
                      <th>VO</th>
                      <th>TAK</th>
                      <th>LIT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.entries.map(entry => {
                      const anc = getAnciennete(entry.created_at);
                      const colCount = 13;
                      const showComment = section.id === 'demandes' && (entry.demande_en_cours_commentaire || entry.demande_en_cours_sujet);
                      return (
                        <React.Fragment key={entry.id}>
                          <tr
                            className="parking-row"
                            onClick={() => onRowClick(entry, section.entries)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td>
                              <span className="plaque-badge">{entry.identifiant || '—'}</span>
                            </td>
                            <td style={{ fontSize: 12, whiteSpace: 'nowrap', textTransform: 'uppercase' }}>
                              {entry.marque || <span style={{ color: '#94a3b8' }}>—</span>}
                            </td>
                            <td>{entry.nom_client || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                            <td onClick={e => e.stopPropagation()}>
                              <InlineStatusSelect
                                value={entry.statut}
                                options={statuts.map(s => ({ value: s.valeur, label: s.nom, color: s.couleur }))}
                                onChange={onStatusChange ? (val) => onStatusChange(entry.id, val) : undefined}
                                disabled={!onStatusChange}
                              />
                            </td>
                            <td className={`age-cell ${anc.cls}`} style={{ whiteSpace: 'nowrap' }}>{anc.label}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                <span>{entry.or_date_creation ? formatDate(entry.or_date_creation) : <span style={{ color: '#94a3b8' }}>—</span>}</span>
                                {entry.or_en_cours_id && <span className="alert-badge badge-or">OR</span>}
                              </div>
                            </td>
                            <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                              {entry.statut_commande
                                ? ({ en_commande: 'En commande', recu_partiellement: 'Reçu part.', livre: 'Reçu' }[entry.statut_commande] || entry.statut_commande)
                                : <span style={{ color: '#94a3b8' }}>—</span>}
                            </td>
                            <td style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                <span>{entry.derniere_facture_date ? formatDate(entry.derniere_facture_date) : <span style={{ color: '#94a3b8' }}>—</span>}</span>
                                {facturesBadgeMap[(entry.identifiant || '').toUpperCase()] && (
                                  <span
                                    className="alert-badge badge-facture badge-facture-clickable"
                                    title="Voir la dernière facture atelier"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setFactureDetail(facturesBadgeMap[(entry.identifiant || '').toUpperCase()])}
                                  >FAC</span>
                                )}
                              </div>
                            </td>
                            <td style={{ fontSize: 12 }}>
                              {facturesBadgeMap[(entry.identifiant || '').toUpperCase()]?.receptionnaire || <span style={{ color: '#94a3b8' }}>—</span>}
                            </td>
                            <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                              <PlanificationCell dossier={entry} onOuvrir={onOuvrirDossier} />
                            </td>
                            <td>{entry.has_vo ? <span className="alert-badge badge-vo">VO</span> : null}</td>
                            <td>{entry.has_takata ? <span className="alert-badge badge-takata">TAK</span> : null}</td>
                            <td>{entry.litige_id ? <span className="alert-badge badge-litige">LIT</span> : null}</td>
                          </tr>
                          {showComment && (
                            <tr className="rapport-comment-row" onClick={() => onRowClick(entry, section.entries)} style={{ cursor: 'pointer' }}>
                              <td colSpan={colCount}>
                                <div className="rapport-comment">
                                  <span
                                    className="alert-badge badge-demande badge-demande-clickable"
                                    title="Ouvrir la demande"
                                    onClick={e => { e.stopPropagation(); setDemandeModalId(entry.demande_en_cours_id); }}
                                  >DEM</span>
                                  {entry.demande_en_cours_sujet && <span className="rapport-comment-sujet">{entry.demande_en_cours_sujet}</span>}
                                  {entry.demande_en_cours_commentaire && <span className="rapport-comment-text">{entry.demande_en_cours_commentaire}</span>}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Message si aucun véhicule classifié */}
      {totalTagged === 0 && (
        <div className="rapport-empty">
          Aucun véhicule ne correspond aux critères de classification.
          Vérifiez vos filtres ou la concession sélectionnée.
        </div>
      )}

      {/* Panneau consultation demande */}
      {demandeModalId && (
        <DemandePanel
          demandeId={demandeModalId}
          user={user}
          onClose={() => setDemandeModalId(null)}
          onArchived={onRefresh}
        />
      )}

      {/* Modale détail facture */}
      {factureDetail && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setFactureDetail(null)}
        >
          <div
            style={{ background: 'var(--bg-surface)', borderRadius: '10px', width: '100%', maxWidth: '780px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border-default)' }}>
              <div>
                <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>🧾 {factureDetail.num_facture}</span>
                <span style={{ marginLeft: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                  {(() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })()} · OR : {factureDetail.num_or || '—'}
                </span>
              </div>
              <button onClick={() => setFactureDetail(null)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--text-secondary)' }}>✕</button>
            </div>
            <div style={{ padding: '16px 20px', overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: '10px', marginBottom: '16px' }}>
                {[
                  { label: 'Client',           value: factureDetail.nom_client },
                  { label: 'Immatriculation',  value: factureDetail.immat },
                  { label: 'VIN',              value: factureDetail.vin },
                  { label: 'Marque',           value: factureDetail.marque },
                  { label: 'Société',          value: factureDetail.societe },
                  { label: 'Département',      value: factureDetail.department },
                  { label: 'Réceptionnaire',   value: factureDetail.receptionnaire },
                  { label: 'Ouverture OR',     value: (() => { const d = factureDetail.date_ouverture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                  { label: 'Date facture',     value: (() => { const d = factureDetail.date_facture; if (!d) return '—'; const [y,m,day] = d.split('T')[0].split('-').map(Number); return new Date(y, m-1, day).toLocaleDateString('fr-FR'); })() },
                  { label: 'Montant HT total', value: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht), highlight: true },
                ].map(item => (
                  <div key={item.label} style={{ background: 'var(--bg-muted)', borderRadius: '6px', padding: '10px 12px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '3px' }}>{item.label}</div>
                    <div style={{ fontWeight: item.highlight ? 700 : 500, color: item.highlight ? 'var(--color-success)' : 'var(--text-primary)', fontSize: item.highlight ? '15px' : '13px' }}>
                      {item.value || '—'}
                    </div>
                  </div>
                ))}
              </div>
              {factureDetail.lignes_json && factureDetail.lignes_json.length > 0 && (
                <>
                  <h3 style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                    Lignes ({factureDetail.lignes_json.length})
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead>
                      <tr style={{ background: 'var(--bg-muted)' }}>
                        {['Prestation', 'Désignation', 'Réf.', 'Qté', 'Montant HT'].map(h => (
                          <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-primary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {factureDetail.lignes_json.map((l, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 10px' }}>
                            <span style={{
                              background: l.prestation === 'M.O.' ? '#fef3c7' : '#eff6ff',
                              color:      l.prestation === 'M.O.' ? '#92400e' : '#1d4ed8',
                              padding: '1px 6px', borderRadius: '4px', fontWeight: 600, fontSize: '11px',
                            }}>{l.prestation || '—'}</span>
                          </td>
                          <td style={{ padding: '6px 10px', color: 'var(--text-primary)' }}>{l.designation || '—'}</td>
                          <td style={{ padding: '6px 10px', fontFamily: 'monospace', color: 'var(--text-secondary)', fontSize: '11px' }}>{l.ref || '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right' }}>{l.qte != null ? l.qte : '—'}</td>
                          <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600, color: parseFloat(l.montant_ht) < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                            {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(l.montant_ht)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ background: 'var(--bg-muted)', borderTop: '2px solid var(--border-default)' }}>
                        <td colSpan={4} style={{ padding: '8px 10px', fontWeight: 700, textAlign: 'right', fontSize: '13px' }}>Total HT</td>
                        <td style={{ padding: '8px 10px', fontWeight: 700, fontSize: '14px', textAlign: 'right', color: parseFloat(factureDetail.montant_ht) < 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(factureDetail.montant_ht)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
